import type {
  AuthStateData,
  CollectionResponse,
  CreateProjectPayload,
  CreateTaskPayload,
  DataResponse,
  HealthResponse,
  LoginPayload,
  LoginResultData,
  LogoutResultData,
  Project,
  Task,
  TaskTimeSnapshot,
  TimerActionResponse,
  UpdateProjectPayload,
  UpdateTaskPayload,
} from "./types";

import type {
  PlanningApprovalPayload,
  PlanningApprovalResponse,
  PlanningData,
} from "./types";


interface ApiErrorPayload {
  message?: string;
  code?: string;
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly code: string | null;

  public constructor(
    status: number,
    message: string,
    code: string | null,
  ) {
    super(message);

    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(path, {
    ...options,

    credentials: "same-origin",

    headers: {
      Accept: "application/json",
      ...options?.headers,
    },
  });

  let payload: unknown = null;

  const contentType =
    response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const errorPayload =
      payload as ApiErrorPayload | null;

    throw new ApiRequestError(
      response.status,

      errorPayload?.message
        ?? `Ошибка запроса: HTTP ${response.status}`,

      errorPayload?.code ?? null,
    );
  }

  return payload as T;
}

export function getHealth(): Promise<HealthResponse> {
  return request<HealthResponse>("/api/health");
}

export function getProjects(): Promise<
  CollectionResponse<Project>
> {
  return request<CollectionResponse<Project>>(
    "/api/projects",
  );
}

export function getAllProjects(): Promise<
  CollectionResponse<Project>
> {
  return request<CollectionResponse<Project>>(
    "/api/projects?includeArchived=true",
  );
}

export function updateProject(
  projectId: number,
  payload: UpdateProjectPayload,
): Promise<DataResponse<Project>> {
  return request<DataResponse<Project>>(
    `/api/projects/${projectId}`,
    {
      method: "PATCH",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify(payload),
    },
  );
}

export function getTasks(): Promise<
  CollectionResponse<Task>
> {
  return request<CollectionResponse<Task>>(
    "/api/tasks",
  );
}

export function getAllTasks(): Promise<
  CollectionResponse<Task>
> {
  return request<CollectionResponse<Task>>(
    "/api/tasks?includeFinished=true",
  );
}

export function getTaskTime(
  taskId: number,
): Promise<DataResponse<TaskTimeSnapshot>> {
  return request<DataResponse<TaskTimeSnapshot>>(
    `/api/tasks/${taskId}/time`,
  );
}

export function updateTask(
  taskId: number,
  payload: UpdateTaskPayload,
): Promise<DataResponse<Task>> {
  return request<DataResponse<Task>>(
    `/api/tasks/${taskId}`,
    {
      method: "PATCH",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify(payload),
    },
  );
}

export function startTaskTimer(
  taskId: number,
): Promise<TimerActionResponse> {
  return request<TimerActionResponse>(
    `/api/tasks/${taskId}/timer/start`,
    {
      method: "POST",
    },
  );
}

export function pauseTaskTimer(
  taskId: number,
): Promise<
  DataResponse<TaskTimeSnapshot>
> {
  return request<DataResponse<TaskTimeSnapshot>>(
    `/api/tasks/${taskId}/timer/pause`,
    {
      method: "POST",
    },
  );
}

export function completeTaskTimer(
  taskId: number,
): Promise<
  DataResponse<TaskTimeSnapshot>
> {
  return request<DataResponse<TaskTimeSnapshot>>(
    `/api/tasks/${taskId}/timer/complete`,
    {
      method: "POST",
    },
  );
}

export function failTaskTimer(
  taskId: number,
): Promise<
  DataResponse<TaskTimeSnapshot>
> {
  return request<DataResponse<TaskTimeSnapshot>>(
    `/api/tasks/${taskId}/timer/fail`,
    {
      method: "POST",
    },
  );
}

export function createProject(
  payload: CreateProjectPayload,
): Promise<DataResponse<Project>> {
  return request<DataResponse<Project>>(
    "/api/projects",
    {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify(payload),
    },
  );
}

export function createTask(
  payload: CreateTaskPayload,
): Promise<DataResponse<Task>> {
  return request<DataResponse<Task>>(
    "/api/tasks",
    {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify(payload),
    },
  );
}

export function getCurrentUser(): Promise<
  DataResponse<AuthStateData>
> {
  return request<DataResponse<AuthStateData>>(
    "/api/auth/me",
  );
}

export function login(
  payload: LoginPayload,
): Promise<DataResponse<LoginResultData>> {
  return request<DataResponse<LoginResultData>>(
    "/api/auth/login",
    {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify(payload),
    },
  );
}

export function logout(): Promise<
  DataResponse<LogoutResultData>
> {
  return request<DataResponse<LogoutResultData>>(
    "/api/auth/logout",
    {
      method: "POST",
    },
  );
}

export function getPlanning(
  date: string,
): Promise<DataResponse<PlanningData>> {
  return request<DataResponse<PlanningData>>(
    `/api/planning?date=${encodeURIComponent(date)}`,
  );
}

export function approvePlanning(
  date: string,
  payload: PlanningApprovalPayload,
): Promise<PlanningApprovalResponse> {
  return request<PlanningApprovalResponse>(
    `/api/planning/${encodeURIComponent(date)}/approve`,
    {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify(payload),
    },
  );
}

export function reopenPlanning(
  date: string,
): Promise<DataResponse<PlanningData>> {
  return request<DataResponse<PlanningData>>(
    `/api/planning/${encodeURIComponent(date)}/reopen`,
    {
      method: "POST",
    },
  );
}
